import { useCurrentUser } from '../contexts/CurrentUser';

/**
 * The modals opened from the Overview widgets and the topic-modeling page carry publication
 * filters (languages, sentiment, content types, dates, events/opinions) on top of the actor ones.
 * Serving those from Elasticsearch is gated by its own workspace flag. Share views carry no
 * workspace config and keep these modals on the v1 endpoints.
 */
export const NARRATIVE_CONTENT_MODALS_ES_FLAG = 'narrative_content_modals_es';

export const useNarrativeContentModalsEs = (isShare = false): boolean => {
  const [currentUser] = useCurrentUser();
  return (
    !isShare && Boolean(currentUser?.workspace?.config?.featureflags?.includes(NARRATIVE_CONTENT_MODALS_ES_FLAG))
  );
};

export const narrativeActorsUrl = (
  narrativeId: string | number,
  resource: string,
  urlParams?: URLSearchParams,
): string => {
  const query = urlParams ? urlParams.toString() : '';
  return `/API/v2/narrative-actors/${narrativeId}/${resource}${query ? `?${query}` : ''}`;
};

export const narrativeMessagesSearchUrl = (narrativeId: string | number): string =>
  `/API/v2/narratives/${narrativeId}/messages/search`;

// The publication filters only the content-modals flag serves from Elasticsearch.
const MODAL_MESSAGE_FILTERS = [
  'languages',
  'topic_modeling_event_ids',
  'topic_modeling_opinion_ids',
  'sentiment',
  'content_types',
  'start',
  'end',
];

export const rollupServesModal = (urlParams: URLSearchParams, contentModalsEs: boolean): boolean =>
  contentModalsEs || !MODAL_MESSAGE_FILTERS.some((key) => urlParams.has(key));

// v1 `/sources` filters the modal sends -> `/actors` filters.
const SOURCES_PARAMS: Record<string, string | null> = {
  narrative_id: null,
  country_ids: 'affiliation_country_ids',
  country_id: 'affiliation_country_ids',
  entity_type: 'entity_types',
  source_group_id: 'source_group_ids',
};

const SOURCES_EXCLUSIVE_FILTERS: Record<string, string | null> = {
  country_ids: 'affiliation',
  country_id: 'affiliation',
  affiliation_country_ids: 'affiliation',
  discreditated: 'discreditation',
  source_bots: 'bots',
  source_group_id: 'source_group_ids',
  audience_lte: 'audience',
  audience_gte: 'audience',
};

// v1 `/sources/by/posts` filters the tops widget sends -> `/actors` filters (`country_ids` = origin here).
const TOP_ACTORS_PARAMS: Record<string, string | null> = {
  country_ids: 'origin_country_ids',
};

const TOP_ACTORS_EXCLUSIVE_FILTERS: Record<string, string | null> = {
  audience_lte: 'audience',
  audience_gte: 'audience',
  country_ids: 'origin_country_ids',
  affiliation_country_ids: 'affiliation',
  discreditated: 'discreditation',
  source_bots: 'bots',
};

const translate = (
  urlParams: URLSearchParams,
  params: Record<string, string | null>,
  exclusiveFilters: Record<string, string | null>,
): URLSearchParams => {
  const translated = new URLSearchParams();
  const exclusive: string[] = [];

  urlParams.forEach((value, key) => {
    if (key === 'exclusive_filters') {
      const mapped = value in exclusiveFilters ? exclusiveFilters[value] : value;
      if (mapped && !exclusive.includes(mapped)) exclusive.push(mapped);
      return;
    }

    const mapped = key in params ? params[key] : key;
    if (mapped) translated.append(mapped, value);
  });

  exclusive.forEach((name) => translated.append('exclusive_filters', name));
  return translated;
};

export const toRollupActorsParams = (urlParams: URLSearchParams): URLSearchParams =>
  translate(urlParams, SOURCES_PARAMS, SOURCES_EXCLUSIVE_FILTERS);

export const toRollupTopActorsParams = (urlParams: URLSearchParams): URLSearchParams =>
  translate(urlParams, TOP_ACTORS_PARAMS, TOP_ACTORS_EXCLUSIVE_FILTERS);

// v1 `/messages` query params the modal builds -> `messages/search` body fields.
const MESSAGE_LIST_FIELDS: Record<string, string> = {
  source_types: 'platforms',
  origin_country_ids: 'origin_country_ids',
  country_id: 'state_affiliated_country_ids',
  country_ids: 'state_affiliated_country_ids',
  affiliation_types: 'affiliation_types',
  entity_type: 'discreditated_entity_types',
  source_group_id: 'source_group_ids',
  source_id: 'source_ids',
  tags: 'tags',
  languages: 'languages',
  sentiment: 'sentiment',
  content_types: 'content_types',
  message_status: 'message_status',
  topic_modeling_event_ids: 'topic_modeling_event_ids',
  topic_modeling_opinion_ids: 'topic_modeling_opinion_ids',
};

const MESSAGE_INT_LIST_FIELDS = new Set([
  'origin_country_ids',
  'state_affiliated_country_ids',
  'source_group_ids',
  'source_ids',
  'topic_modeling_event_ids',
  'topic_modeling_opinion_ids',
]);

const MESSAGE_SCALAR_FIELDS: Record<string, string> = {
  source_audience_gte: 'audience_gte',
  source_audience_lte: 'audience_lte',
  is_verified: 'is_verified',
  influential: 'influential',
  big_outlets: 'big_outlets',
  discreditated: 'discreditated',
  is_bot: 'bots',
  suspended_by_platform: 'suspended_by_platform',
  inauthentic_behavior: 'inauthentic_behavior',
  immature_account: 'immature_account',
  include_unclassified: 'include_unclassified',
  favorite: 'favorite',
};

const MESSAGE_INT_SCALAR_FIELDS = new Set(['audience_gte', 'audience_lte']);

const MESSAGE_EXCLUSIVE_FILTERS: Record<string, string[]> = {
  source_origin_country_ids: ['origin_country_ids'],
  languages: ['languages'],
  source_state_affiliated_country_ids: ['state_affiliated_country_ids', 'state_affiliated_all_countries'],
  source_state_affiliated_all_countries: ['state_affiliated_all_countries'],
  source_affiliation_types: ['affiliation_types'],
  discreditated: ['discreditation', 'discreditated_entity_types'],
  source_discreditated_entity_types: ['discreditated_entity_types'],
  source_bots: ['bots'],
  source_group_ids: ['source_group_ids'],
  source_ids: ['source_ids'],
};

const parseScalar = (value: string, asInt: boolean): number | boolean | string => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return asInt ? Number(value) : value;
};

/** Body for `messages/search` built from the v1-shaped params the modal already assembles. */
export const toMessagesSearchBody = (urlParams: URLSearchParams, cursor?: string | null): Record<string, unknown> => {
  // v1 `/messages` matches the author (actor, else source) only, so the modal keeps that here.
  const body: Record<string, unknown> = { search_by_source_or_actor: false };
  const lists: Record<string, Array<string | number>> = {};
  const exclusive: string[] = [];

  urlParams.forEach((value, key) => {
    if (key === 'exclusive_filters') {
      (MESSAGE_EXCLUSIVE_FILTERS[value] ?? [value]).forEach((name) => {
        if (!exclusive.includes(name)) exclusive.push(name);
      });
      return;
    }
    if (key in MESSAGE_LIST_FIELDS) {
      const field = MESSAGE_LIST_FIELDS[key];
      (lists[field] ??= []).push(MESSAGE_INT_LIST_FIELDS.has(field) ? Number(value) : value);
      return;
    }
    if (key in MESSAGE_SCALAR_FIELDS) {
      const field = MESSAGE_SCALAR_FIELDS[key];
      body[field] = parseScalar(value, MESSAGE_INT_SCALAR_FIELDS.has(field));
      return;
    }
    if (key === 'state_affiliated' && value === 'true') body.state_affiliated_all_countries = true;
    if (key === 'start') body.start_date = value.slice(0, 10);
    if (key === 'end') body.end_date = value.slice(0, 10);
    if (key === 'sorting') body.sort = [value];
    if (key === 'size') body.page_size = Number(value);
  });

  Object.assign(body, lists);
  if (exclusive.length > 0) body.exclusive_filters = exclusive;
  if (cursor) body.cursor = cursor;
  return body;
};
